import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { getStoreCatalogue } from '../api/stockLedger';
import { inputSx } from '../theme';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function normalizeCatalogueItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export function catalogueAvgMrp(item) {
  const batches = item?.batches || [];
  const mrps = batches
    .map(b => Number(b.mrp))
    .filter(n => Number.isFinite(n) && n >= 0);
  if (!mrps.length) return null;
  const avg = mrps.reduce((sum, n) => sum + n, 0) / mrps.length;
  return avg.toFixed(2);
}

export function catalogueItemLabel(item) {
  if (!item) return '';
  const name = item.item_name || item.item_code || 'Item';
  const code = item.item_code ? ` (${item.item_code})` : '';
  const avail = item.total_available_quantity != null
    ? ` · Avail: ${item.total_available_quantity}`
    : '';
  const avgMrp = catalogueAvgMrp(item);
  const mrp = avgMrp != null ? ` · Avg. MRP: ${avgMrp}` : '';
  return `${name}${code}${avail}${mrp}`;
}

/** @deprecated use catalogueItemLabel */
export const stockBalanceLabel = catalogueItemLabel;

function itemKey(item) {
  return item?.item_id || '';
}

/** Debounced store-catalogue search — min 2 chars, item-level options with total availability. */
export default function StockBalanceAutocomplete({
  storeId,
  value,
  onChange,
  label,
  placeholder = 'Search item…',
  disabled = false,
  inputRef,
  onKeyDown,
  sx,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    if (selectedItem && itemKey(selectedItem) === value) return;
    const found = options.find(item => itemKey(item) === value) || null;
    if (found) setSelectedItem(found);
  }, [value, options, selectedItem]);

  const isSearching = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!open || !storeId) return;

    const inputQuery = inputValue.trim();
    const query = debouncedSearch;
    const shouldSearch = query.length >= MIN_SEARCH_LENGTH;
    const pendingSearch = inputQuery.length >= MIN_SEARCH_LENGTH && inputQuery !== query;

    if (pendingSearch) {
      setOptions([]);
      return;
    }

    if (!shouldSearch) {
      setOptions([]);
      return;
    }

    const requestId = ++requestIdRef.current;

    async function fetchCatalogue() {
      setOptions([]);
      setLoading(true);
      try {
        // API: GET /inventory/stock/store-catalogue/
        const data = await getStoreCatalogue({
          store_id: storeId,
          search: query,
          // Expired batches can never be transferred or dispensed, so they must not
          // count towards the availability shown on the option.
          exclude_expired: true,
          include_uom_name: true,
        });
        if (requestId !== requestIdRef.current) return;
        setOptions(normalizeCatalogueItems(data));
      } catch {
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }

    fetchCatalogue();
  }, [debouncedSearch, open, storeId, inputValue]);

  const displayOptions = useMemo(() => {
    if (isSearching || !selectedItem) return options;
    const key = itemKey(selectedItem);
    if (options.some(item => itemKey(item) === key)) return options;
    return [selectedItem, ...options];
  }, [options, selectedItem, isSearching]);

  const noOptionsText = (() => {
    if (!storeId) return 'Select a store first';
    if (loading) return 'Searching…';
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters`;
    }
    return 'No stock found';
  })();

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled || !storeId}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      loading={loading}
      value={selectedItem}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
          if (
            (reason === 'input' || reason === 'clear')
            && selectedItem
            && newInput.trim() !== catalogueItemLabel(selectedItem)
          ) {
            setSelectedItem(null);
            onChange('', null);
          }
        }
      }}
      onChange={(_, item) => {
        setSelectedItem(item);
        onChange(item ? itemKey(item) : '', item);
      }}
      getOptionLabel={catalogueItemLabel}
      isOptionEqualToValue={(a, b) => itemKey(a) === itemKey(b)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          inputRef={inputRef}
          onKeyDown={onKeyDown}
          InputLabelProps={label ? { shrink: true } : undefined}
          sx={{ ...inputSx, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
